import React, { useEffect, useState, useCallback, useRef } from "react";
import * as cocoSsd from "@tensorflow-models/coco-ssd";
import * as tf from "@tensorflow/tfjs";
import Timer from "../Components/Timer";
import {
  useFinalSubmitAssignmentMutation,
  useGetAssignmentQuery,
  useGetAssignmentSubmitQuery,
  useGetCampusQuery,
  useGetStudentByEmailIdQuery,
} from "../../../graphql/generated.tsx";
import Detection from "../Components/Detection/index.jsx";
import swal from "sweetalert";
import { Button, Modal, Spinner } from "flowbite-react";
import { useNavigate } from "react-router-dom";
import { HiOutlineExclamationCircle, HiCheckCircle, HiXCircle } from "react-icons/hi";
import AssignmentLayout from "./index.jsx";
import ModalComponent from "../Components/IconInfoRow.jsx";
import ModalUI from "../Components/ModalUI.jsx";
import "./TestAlert.css";
import PanicPopup from "./Panicpopup.jsx";
// NetworkIndicator removed per request

const TestLayout = ({ children, assignmentId, disableSubmit, previewMode, submitRef, topBarLeft }) => {
  const [modalType, setModalType] = useState("submit");
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const [showPanicPopup, setShowPanicPopup] = useState(false);
  const [showBotWarning, setShowBotWarning] = useState(false);
  const [showTenMinReminder, setShowTenMinReminder] = useState(false);
  const [showSubmitProgressModal, setShowSubmitProgressModal] = useState(false);
  const [submitProgressStage, setSubmitProgressStage] = useState("loading");
  const [submitProgressSubmission, setSubmitProgressSubmission] = useState(null);
  const [submitProgressTextVisible, setSubmitProgressTextVisible] = useState(false);
  const [submitProgressButtonVisible, setSubmitProgressButtonVisible] = useState(false);
  const [submitProgressTitle, setSubmitProgressTitle] = useState("Submitted!");
  const [submitProgressMessage, setSubmitProgressMessage] = useState("Your assignment has been submitted successfully.");
  const [submitProgressReason, setSubmitProgressReason] = useState("");
  const botWarningResolverRef = useRef(null);
  const compatFlagKey = `assignment_${assignmentId}_compat_ok`;
  const [testStarted, setTestStarted] = useState(() => sessionStorage.getItem(compatFlagKey) === 'true');
  const videoRef = useRef(null);
  const previewStreamRef = useRef(null);

  // ===================== COMPATIBILITY CHECK STATES =====================
  const [isCheckingCompatibility, setIsCheckingCompatibility] = useState(true);
  const [cameraCheck, setCameraCheck] = useState(false);
  const [internetCheck, setInternetCheck] = useState(false);
  const [browserCheck, setBrowserCheck] = useState(false);
  const [plainBackgroundCheck, setPlainBackgroundCheck] = useState(false);
  const [notificationsOff, setNotificationsOff] = useState(false);
  const [hasBattery, setHasBattery] = useState(false);
  const [compatibilityMessage, setCompatibilityMessage] = useState("");
  const [internetStatus, setInternetStatus] = useState('idle');
  const [cameraStatus, setCameraStatus] = useState('idle');
  const [browserStatus, setBrowserStatus] = useState('idle');
  const [backgroundStatus, setBackgroundStatus] = useState('idle');

  // ===================== GRAPHQL DATA FETCH =====================
  const { data: assignmentData, loading } = useGetAssignmentQuery({
    variables: { id: assignmentId },
  });

  const proctoringEnabled = !!assignmentData?.getAssignment?.enable_proctoring;
  const compatibilityPassed = proctoringEnabled
    ? (cameraCheck && internetCheck && browserCheck && plainBackgroundCheck && notificationsOff && hasBattery)
    : (internetCheck && browserCheck);

  const stopCameraPreview = useCallback(() => {
    if (previewStreamRef.current) {
      previewStreamRef.current.getTracks().forEach((track) => track.stop());
      previewStreamRef.current = null;
    }
    if (videoRef.current) {
      try {
        videoRef.current.srcObject = null;
      } catch (err) {
        console.error("Failed to clear camera preview:", err);
      }
    }
  }, []);

  useEffect(() => {
    if (!proctoringEnabled) {
      stopCameraPreview();
      return;
    }
    if (testStarted) {
      stopCameraPreview();
      return;
    }
    let cancelled = false;
    const attachAndPlay = (stream) => {
      if (!videoRef.current) return;
      videoRef.current.srcObject = stream;
      const playPromise = videoRef.current.play?.();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise.catch(() => {});
      }
    };
    const startPreview = async () => {
      if (previewStreamRef.current) {
        attachAndPlay(previewStreamRef.current);
        return;
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        console.error("Camera preview not supported in this browser.");
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        previewStreamRef.current = stream;
        attachAndPlay(stream);
      } catch (err) {
        console.error("Camera preview failed:", err);
      }
    };
    if (videoRef.current) {
      startPreview();
    } else {
      const id = requestAnimationFrame(() => {
        if (!cancelled && videoRef.current) startPreview();
      });
      return () => {
        cancelled = true;
        cancelAnimationFrame(id);
        stopCameraPreview();
      };
    }
    return () => {
      cancelled = true;
      stopCameraPreview();
    };
  }, [testStarted, stopCameraPreview, isCheckingCompatibility, proctoringEnabled]);

  const studentEmail = JSON.parse(localStorage.getItem("user"))?.email;
  const { data: studentData } = useGetStudentByEmailIdQuery({
    variables: { email: studentEmail },
  });

  const cheatCount = parseInt(sessionStorage.getItem("count_facedetect") || "0", 10);
  const tabSwitches = parseInt(sessionStorage.getItem("count_tabchange") || "0", 10);

  const { data: campus } = useGetCampusQuery({
    variables: { id: studentData?.getStudentByEmailId?.campusId },
    skip: !studentData?.getStudentByEmailId,
  });

  const { data: AssignmentCompleted } = useGetAssignmentSubmitQuery({
    variables: {
      assignmentId,
      studentId: studentData?.getStudentByEmailId?.id,
      campus_name: campus?.campus?.name,
    },
    skip: !studentData?.getStudentByEmailId || !campus?.campus,
  });

  const [finalSubmitAssignment] = useFinalSubmitAssignmentMutation();

  const getCheatReasonsKey = useCallback(() => {
    const studentId = studentData?.getStudentByEmailId?.id || "unknown";
    return `cheat_reasons_${studentId}_${assignmentId}`;
  }, [studentData, assignmentId]);

  // ===================== 58-MIN REMINDER =====================
  const warn58Ref = useRef(null);
  const warnShownRef = useRef(false);
  const timeAutoSubmitTriggeredRef = useRef(false);

  // ===================== COMPATIBILITY CHECK LOGIC =====================
  const checkInternetSpeed = async () => {
    const image = new Image();
    const startTime = new Date().getTime();
    const imageUrl =
      "https://upload.wikimedia.org/wikipedia/commons/3/3f/JPEG_example_flower.jpg?rand=" +
      Math.random();
    return new Promise((resolve) => {
      image.onload = () => {
        const endTime = new Date().getTime();
        const duration = (endTime - startTime) / 1000;
        const imageSizeInBits = 500000 * 8;
        const speedMbps = imageSizeInBits / (duration * 1024 * 1024);
        resolve(speedMbps);
      };
      image.onerror = () => resolve(0);
      image.src = imageUrl;
    });
  };

  const checkCameraAndMic = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      return true;
    } catch (err) {
      return false;
    }
  };

  const checkBrowserVersion = () => {
    const userAgent = navigator.userAgent;
    const isChrome = /Chrome/.test(userAgent) && /Google Inc/.test(navigator.vendor);
    if (isChrome) {
      const versionMatch = userAgent.match(/Chrome\/(\d+)/);
      const version = versionMatch ? parseInt(versionMatch[1]) : 0;
      return version >= 80;
    }
    return true;
  };

  // ===================== PLAIN BACKGROUND CHECK =====================
  const checkPlainBackground = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      const video = document.createElement("video");
      video.srcObject = stream;
      await new Promise((resolve) => {
        video.onloadedmetadata = () => {
          video.play();
          resolve();
        };
      });
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = 320;
      canvas.height = 240;
      ctx.drawImage(video, 0, 0, 320, 240);
      const model = await cocoSsd.load();
      const predictions = await model.detect(canvas);
      const person = predictions.find((p) => p.class === "person" && p.score > 0.5);
      if (!person) {
        stream.getTracks().forEach((t) => t.stop());
        return false;
      }
      const frame = ctx.getImageData(0, 0, 320, 240);
      const data = frame.data;
      let sum = 0, sumSq = 0, count = 0;
      for (let y = 0; y < 240; y++) {
        for (let x = 0; x < 320; x++) {
          if (
            x < person.bbox[0] ||
            x > person.bbox[0] + person.bbox[2] ||
            y < person.bbox[1] ||
            y > person.bbox[1] + person.bbox[3]
          ) {
            const idx = (y * 320 + x) * 4;
            const r = data[idx];
            const g = data[idx + 1];
            const b = data[idx + 2];
            const brightness = 0.299 * r + 0.587 * g + 0.114 * b;
            sum += brightness;
            sumSq += brightness * brightness;
            count++;
          }
        }
      }
      const mean = sum / count;
      const variance = sumSq / count - mean * mean;
      stream.getTracks().forEach((t) => t.stop());
      return variance < 1500;
    } catch (e) {
      console.error("Plain background check failed:", e);
      return false;
    }
  };

  const performCompatibilityCheck = useCallback(async () => {
    setIsCheckingCompatibility(true);
    setCompatibilityMessage("Running system checks...");
    setInternetStatus('checking');
    setCameraStatus('idle');
    setBrowserStatus('idle');
    setBackgroundStatus('idle');

    await new Promise(r => setTimeout(r, 50));
    const internetStart = Date.now();
    const speed = await checkInternetSpeed();
    const internetOk = speed >= 2;
    setInternetCheck(internetOk);
    const internetElapsed = Date.now() - internetStart;
    if (internetElapsed < 2000) {
      await new Promise(r => setTimeout(r, 2000 - internetElapsed));
    }
    setInternetStatus(internetOk ? 'pass' : 'fail');

    setBrowserStatus('checking');
    await new Promise(r => setTimeout(r, 50));
    const browserStart = Date.now();
    const browserOK = checkBrowserVersion();
    setBrowserCheck(browserOK);
    const browserElapsed = Date.now() - browserStart;
    if (browserElapsed < 2000) {
      await new Promise(r => setTimeout(r, 2000 - browserElapsed));
    }
    setBrowserStatus(browserOK ? 'pass' : 'fail');

    if (proctoringEnabled) {
      setCameraStatus('checking');
      await new Promise(r => setTimeout(r, 50));
      const cameraStart = Date.now();
      try {
        const camMicOK = await checkCameraAndMic();
        setCameraCheck(camMicOK);
        const cameraElapsed = Date.now() - cameraStart;
        if (cameraElapsed < 2000) {
          await new Promise(r => setTimeout(r, 2000 - cameraElapsed));
        }
        setCameraStatus(camMicOK ? 'pass' : 'fail');
      } catch {
        setCameraCheck(false);
        const cameraElapsed = Date.now() - cameraStart;
        if (cameraElapsed < 2000) {
          await new Promise(r => setTimeout(r, 2000 - cameraElapsed));
        }
        setCameraStatus('fail');
      }

      setBackgroundStatus('checking');
      await new Promise(r => setTimeout(r, 50));
      const bgStart = Date.now();
      try {
        const bgOK = await checkPlainBackground();
        setPlainBackgroundCheck(bgOK);
        const bgElapsed = Date.now() - bgStart;
        if (bgElapsed < 2000) {
          await new Promise(r => setTimeout(r, 2000 - bgElapsed));
        }
        setBackgroundStatus(bgOK ? 'pass' : 'fail');
      } catch {
        setPlainBackgroundCheck(false);
        const bgElapsed = Date.now() - bgStart;
        if (bgElapsed < 2000) {
          await new Promise(r => setTimeout(r, 2000 - bgElapsed));
        }
        setBackgroundStatus('fail');
      }
    } else {
      setCameraCheck(false);
      setPlainBackgroundCheck(false);
      setCameraStatus('idle');
      setBackgroundStatus('idle');
    }

    setIsCheckingCompatibility(false);
  }, [proctoringEnabled]);

  useEffect(() => {
    if (!testStarted) {
      performCompatibilityCheck();
    }
  }, [performCompatibilityCheck, testStarted]);

  // ===================== HANDLE SUBMIT =====================
  const confirmBotWarning = useCallback(() => {
    return new Promise((resolve) => {
      botWarningResolverRef.current = resolve;
      setShowBotWarning(true);
    });
  }, []);

  const handleBotWarningClose = useCallback((shouldEndTest) => {
    setShowBotWarning(false);
    const resolver = botWarningResolverRef.current;
    botWarningResolverRef.current = null;
    if (resolver) resolver(shouldEndTest);
  }, []);

  const handleTenMinReminderClose = useCallback(() => {
    setShowTenMinReminder(false);
    try { sessionStorage.removeItem('bt_skip_tab_switch'); } catch {}
  }, []);

  const finalizeSubmissionNavigation = useCallback(
    (submission) => {
      setTestStarted(false);
      try {
        sessionStorage.removeItem(compatFlagKey);
        const scopedKey = localStorage.getItem("current_cheat_key") || getCheatReasonsKey();
        localStorage.removeItem(scopedKey);
        localStorage.removeItem("current_cheat_key");
      } catch {}

      const target = `/assignments/${assignmentId}/summary`;
      const redirect = () => {
        navigate(target, { replace: true, state: { submission } });
      };
      if (document.fullscreenElement) {
        document.exitFullscreen().finally(redirect);
      } else {
        redirect();
      }
    },
    [assignmentId, navigate, compatFlagKey, getCheatReasonsKey]
  );

  const handleSubmit = useCallback(
    async (autoType = "manual", options = {}) => {
      const { uiMode = "swal" } = options;
      try {
        const type =
          typeof autoType === "boolean"
            ? autoType
              ? "tab"
              : "manual"
            : autoType;

        const startTimeKey = `assignment_${assignmentId}_start_time`;
        const now = Date.now();
        const startedAt = parseInt(sessionStorage.getItem(startTimeKey) || "0", 10);

        if (!startedAt) {
          sessionStorage.setItem(startTimeKey, now.toString());
        }

        const elapsed = now - (startedAt || now);

        if (type === 'manual' && elapsed < 5 * 60 * 1000) {
          if (uiMode === "progress") {
            setShowSubmitProgressModal(false);
            setSubmitProgressStage("loading");
            setSubmitProgressSubmission(null);
            setSubmitProgressTextVisible(false);
            setSubmitProgressButtonVisible(false);
            setSubmitProgressReason("");
          }
          setOpenModal(false);
          const shouldEndTest = await confirmBotWarning();
          if (!shouldEndTest) {
            if (uiMode === "progress") {
              setModalType("submit");
              setOpenModal(true);
            }
            return;
          }

          // User confirmed early submit warning. Re-open progress modal
          // so they can see loader -> success -> Done before navigation.
          if (uiMode === "progress") {
            setShowSubmitProgressModal(true);
            setSubmitProgressStage("loading");
            setSubmitProgressTextVisible(false);
            setSubmitProgressButtonVisible(false);
            setSubmitProgressReason("");
          }

          const timestamp = new Date().toLocaleString();
          const previous = localStorage.getItem("cheat_reasons_string") || "";
          const entry = `Trying to submit before standby duration | ${timestamp}\n`;
          localStorage.setItem("cheat_reasons_string", previous + entry);
        }

        const scopedKey = localStorage.getItem('current_cheat_key') || getCheatReasonsKey();
        const cheatReasons = localStorage.getItem(scopedKey)?.trim() || "";

        const submitResult = await finalSubmitAssignment({
          variables: {
            AssignmentInput: {
              assignment_id: assignmentId,
              cheat_count: assignmentData?.getAssignment?.enable_proctoring
                ? cheatCount
                : 0,
              cheat_reasons: cheatReasons,
              tabSwitches: assignmentData?.getAssignment?.enable_proctoring
                ? tabSwitches
                : 0,
              student_id: studentData?.getStudentByEmailId?.id,
              campus_name: campus?.campus?.name,
              cluster_id: studentData?.getStudentByEmailId?.clusterIds[0],
            },
          },
        });

        let title = "Submitted!";
        let text = "Your assignment has been submitted successfully.";
        let icon = "success";

        if (type === "tab") {
          title = "Auto Submitted";
          text = "Your test has been auto-submitted as you switched the tab.";
          icon = "warning";
        } else if (type === "proctor") {
          title = "Auto Submitted";
          text = "You crossed the cheat limit.";
          icon = "warning";
        } else if (type === "time") {
          title = "Auto Submitted";
          text = "Time is up. Your test has been auto-submitted.";
          icon = "warning";
        }

        const submission = {
          ...(submitResult?.data?.finalSubmitAssignment || {}),
          assignment_id: assignmentId,
          cheat_reasons: cheatReasons,
          endTime: new Date().toISOString(),
          timeRemaining: 0,
          completed: true,
        };

        const shouldUseProgressUi =
          (uiMode === "progress" && type === "manual") ||
          type === "tab" ||
          type === "proctor" ||
          type === "time";

        if (shouldUseProgressUi) {
          try {
            sessionStorage.setItem("bt_submit_ui_pending", "1");
          } catch {}
          setShowSubmitProgressModal(true);
          setSubmitProgressStage("loading");
          setSubmitProgressSubmission(submission);
          setSubmitProgressTitle(title);
          setSubmitProgressMessage(text);
          setSubmitProgressReason(
            type === "tab"
              ? "Reason: Tab switch detected."
              : type === "time"
              ? "Reason: Time limit exceeded."
              : type === "proctor"
              ? "Reason: Cheat limit exceeded."
              : ""
          );
          setSubmitProgressTextVisible(false);
          setSubmitProgressButtonVisible(false);

          // Keep loader visible briefly, then reveal success UI.
          setTimeout(() => {
            setSubmitProgressStage("success");
            setTimeout(() => setSubmitProgressTextVisible(true), 220);
            setTimeout(() => setSubmitProgressButtonVisible(true), 520);
          }, 800);
          return;
        }

        swal({
          title,
          text,
          icon,
          buttons: {
            confirm: {
              text: "OK",
              className: "swal-button--confirm",
              closeModal: true,
            },
          },
          closeOnClickOutside: false,
          closeOnEsc: false,
        }).then(() => {
          finalizeSubmissionNavigation(submission);
        });
      } catch (error) {
        if (options?.uiMode === "progress") {
          setShowSubmitProgressModal(false);
          setSubmitProgressStage("loading");
          setSubmitProgressSubmission(null);
          setSubmitProgressTextVisible(false);
          setSubmitProgressButtonVisible(false);
          setSubmitProgressReason("");
        }
        console.error(error);
      }
    },
    [
      finalSubmitAssignment,
      assignmentId,
      cheatCount,
      tabSwitches,
      studentData,
      navigate,
      assignmentData,
      campus,
      confirmBotWarning,
      finalizeSubmissionNavigation,
    ]
  );

  const startSubmitProgressFlow = useCallback(() => {
    try {
      sessionStorage.setItem("bt_submit_ui_pending", "1");
    } catch {}
    setShowSubmitProgressModal(true);
    setSubmitProgressStage("loading");
    setSubmitProgressSubmission(null);
    setSubmitProgressTextVisible(false);
    setSubmitProgressButtonVisible(false);
    setSubmitProgressReason("");
    handleSubmit("manual", { uiMode: "progress" });
  }, [handleSubmit]);

  const handleSubmitByEndtimePassed = useCallback(() => {
    handleSubmit('time');
  }, [handleSubmit]);

  // ===================== ASSIGN SUBMIT REF =====================
  useEffect(() => {
    if (!submitRef) return;
    submitRef.current = {
      triggerSubmit: () => {
        setModalType("submit");
        setOpenModal(true);
      },
      triggerLeave: () => {
        setModalType("leave");
        setOpenModal(true);
      },
      forceSubmit: (autoReason = false) => {
        if (autoReason === 'proctor' || autoReason === 'time' || autoReason === 'tab') {
          handleSubmit(autoReason);
          return;
        }
        handleSubmit(autoReason === true ? 'tab' : 'manual');
      },
    };
    return () => {
      if (submitRef.current) submitRef.current = null;
    };
  }, [submitRef, handleSubmit, startSubmitProgressFlow]);

  const remainingTimeInMs =
    AssignmentCompleted?.getAssignmentSubmit?.timeRemaining ?? 0;
  const dueDateMs = assignmentData?.getAssignment?.due_date
    ? new Date(assignmentData.getAssignment.due_date).getTime()
    : null;
  const dueRemainingTimeInMs = dueDateMs !== null ? Math.max(0, dueDateMs - Date.now()) : null;
  const effectiveRemainingTimeInMs = dueRemainingTimeInMs !== null
    ? Math.min(remainingTimeInMs, dueRemainingTimeInMs)
    : remainingTimeInMs;
  const remainingDurationInHours = effectiveRemainingTimeInMs / (1000 * 60 * 60);

  useEffect(() => {
    if (!testStarted) {
      timeAutoSubmitTriggeredRef.current = false;
      return;
    }
    if (effectiveRemainingTimeInMs > 0) {
      timeAutoSubmitTriggeredRef.current = false;
      return;
    }
    if (timeAutoSubmitTriggeredRef.current) return;
    timeAutoSubmitTriggeredRef.current = true;
    handleSubmitByEndtimePassed();
  }, [testStarted, effectiveRemainingTimeInMs, handleSubmitByEndtimePassed]);

  // ===================== HELPER: get total check count =====================
  const totalChecks = proctoringEnabled ? 4 : 2;
  const passedChecks = [
    internetStatus === 'pass',
    browserStatus === 'pass',
    proctoringEnabled && cameraStatus === 'pass',
    proctoringEnabled && backgroundStatus === 'pass',
  ].filter(Boolean).length;

  const allAutoChecksDone = proctoringEnabled
    ? ['pass', 'fail'].includes(internetStatus) &&
      ['pass', 'fail'].includes(browserStatus) &&
      ['pass', 'fail'].includes(cameraStatus) &&
      ['pass', 'fail'].includes(backgroundStatus)
    : ['pass', 'fail'].includes(internetStatus) &&
      ['pass', 'fail'].includes(browserStatus);

  const progressPct = totalChecks > 0 ? Math.round((passedChecks / totalChecks) * 100) : 0;

  const allAutoPassed = proctoringEnabled
    ? internetStatus === 'pass' && browserStatus === 'pass' && cameraStatus === 'pass' && backgroundStatus === 'pass'
    : internetStatus === 'pass' && browserStatus === 'pass';

  // Auto-navigate when all checks pass
  useEffect(() => {
    if (allAutoPassed && allAutoChecksDone && !isCheckingCompatibility) {
      const autoStartTimer = setTimeout(() => {
        try {
          sessionStorage.setItem(compatFlagKey, 'true');
          sessionStorage.setItem(`assignment_${assignmentId}_start_time`, Date.now().toString());
          const scopedKey = getCheatReasonsKey();
          localStorage.removeItem(scopedKey);
          localStorage.setItem('current_cheat_key', scopedKey);
          const sid = studentData?.getStudentByEmailId?.id;
          if (sid) {
            sessionStorage.removeItem(`tabSwitchCount_${sid}_${assignmentId}`);
          }
        } catch {}
        setTestStarted(true);
      }, 2000);
      return () => clearTimeout(autoStartTimer);
    }
  }, [allAutoPassed, allAutoChecksDone, isCheckingCompatibility, assignmentId, getCheatReasonsKey, studentData, compatFlagKey]);

  // One-shot reminder at 10 minutes remaining
  useEffect(() => {
    if (!testStarted) return;
    if (!remainingTimeInMs || remainingTimeInMs <= 0) return;
    const keyShown = `warn10m_shown_${assignmentId}`;
    if (warnShownRef.current || sessionStorage.getItem(keyShown) === 'true') return;

    const TimeAlert = 10 * 60 * 1000;
    const showReminder = () => {
      if (warnShownRef.current) return;
      warnShownRef.current = true;
      try { sessionStorage.setItem(keyShown, 'true'); } catch {}
      try { sessionStorage.setItem('bt_skip_tab_switch', 'true'); } catch {}
      setShowTenMinReminder(true);
    };

    if (remainingTimeInMs <= TimeAlert) {
      showReminder();
      return;
    }
    const delay = effectiveRemainingTimeInMs - TimeAlert;
    warn58Ref.current = setTimeout(showReminder, delay);
    return () => {
      if (warn58Ref.current) clearTimeout(warn58Ref.current);
    };
  }, [testStarted, effectiveRemainingTimeInMs, assignmentId]);

  if (loading) {
    return (
      <AssignmentLayout>
        <div className="flex items-center justify-center h-screen">
          Loading...
        </div>
      </AssignmentLayout>
    );
  }

  // ===================== CHECK ROW COMPONENT =====================
  const CheckRow = ({ status, label, detail, resultLabel }) => {
    const isPass = status === 'pass';
    const isFail = status === 'fail';
    const isChecking = status === 'checking';
    const isIdle = status === 'idle';

    const rowBg = isPass
      ? { background: '#f0faf5' }
      : isFail
      ? { background: '#fff4f0' }
      : isChecking
      ? {
          background: 'linear-gradient(100deg, #e9ecef 10%, #f8fafc 35%, #e1e5ea 52%, #f8fafc 68%, #e9ecef 92%)',
          backgroundSize: '220% 100%',
          animation: 'compat_shimmerBase 4s linear infinite',
        }
      : { background: 'transparent' };

    const iconBg = isPass
      ? '#E1F5EE'
      : isFail
      ? '#FAECE7'
      : isChecking
      ? 'rgba(255,255,255,0.4)'
      : 'var(--color-background-secondary, #f5f5f5)';

    const iconStroke = isPass ? '#0F6E56' : isFail ? '#993C1D' : isChecking ? '#6b7280' : '#888780';

    const detailColor = isPass ? '#0F6E56' : isFail ? '#993C1D' : isChecking ? '#4b5563' : '#9ca3af';

    const badgeBg = isPass ? '#9FE1CB' : isFail ? '#F5C4B3' : isChecking ? 'rgba(255,255,255,0.55)' : '#e5e7eb';
    const badgeColor = isPass ? '#085041' : isFail ? '#712B13' : isChecking ? '#374151' : '#6b7280';

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          padding: '16px 16px',
          borderRadius: '10px',
          marginBottom: '8px',
          transition: 'background 0.2s',
          ...rowBg,
        }}
      >
        {/* Icon */}
        <div
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {isPass && (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={iconStroke} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
          {isFail && (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={iconStroke} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          )}
          {isChecking && (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
          )}
          {isIdle && (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={iconStroke} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
            </svg>
          )}
        </div>

        {/* Text */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--color-text-primary, #111827)', marginBottom: '2px' }}>
            {label}
          </div>
          {(detail || isChecking) && (
            <div style={{ fontSize: '13px', fontWeight: 500, color: detailColor }}>
              {isChecking ? 'Verifying…' : detail}
            </div>
          )}
        </div>

        {/* Badge */}
        {isChecking ? (
          <span style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', fontWeight: 600, padding: '4px 11px', borderRadius: '20px', background: badgeBg, color: badgeColor, whiteSpace: 'nowrap', flexShrink: 0 }}>
            <span style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
              <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#6b7280', display: 'inline-block', animation: 'compat_dp 1.3s infinite', animationDelay: '0s' }} />
              <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#6b7280', display: 'inline-block', animation: 'compat_dp 1.3s infinite', animationDelay: '0.2s' }} />
              <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#6b7280', display: 'inline-block', animation: 'compat_dp 1.3s infinite', animationDelay: '0.4s' }} />
            </span>
            Checking
          </span>
        ) : !isIdle ? (
          <span style={{ fontSize: '13px', fontWeight: 600, padding: '4px 11px', borderRadius: '20px', background: badgeBg, color: badgeColor, whiteSpace: 'nowrap', flexShrink: 0 }}>
            {resultLabel || (isPass ? 'Pass' : 'Failed')}
          </span>
        ) : (
          <span style={{ fontSize: '13px', fontWeight: 600, padding: '4px 11px', borderRadius: '20px', background: '#e5e7eb', color: '#6b7280', whiteSpace: 'nowrap', flexShrink: 0 }}>
            Pending
          </span>
        )}
      </div>
    );
  };

  // ===================== COMPATIBILITY SCREEN =====================
  if (!testStarted) {
    return (
      <AssignmentLayout>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: '18px', background: 'transparent', fontFamily: "'Poppins', sans-serif" }}>
          <div style={{ width: '100%', maxWidth: '760px', transform: 'translateY(-20px)' }}>

            {/* Card */}
            <div style={{
              background: '#ffffff',
              border: '0.5px solid #e2e8f0',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 16px 34px rgba(15, 23, 42, 0.10), 0 2px 10px rgba(15, 23, 42, 0.08)',
              backgroundImage: isCheckingCompatibility ? 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.3) 50%, rgba(255,255,255,0) 100%)' : 'none',
              backgroundSize: isCheckingCompatibility ? '200% 100%' : '100% 100%',
              backgroundPosition: isCheckingCompatibility ? '-200% 0' : '0 0',
              animation: isCheckingCompatibility ? 'compat_shimmerBase 2s infinite' : 'none',
            }}>
              {/* Header */}
              <div style={{ padding: '34px 32px 28px', borderBottom: '0.5px solid #e2e8f0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '10px' }}>
                  {/* Shield icon */}
                  <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: allAutoPassed && allAutoChecksDone ? '#E1F5EE' : '#eef0f3', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={allAutoPassed && allAutoChecksDone ? '#0F6E56' : '#6b7280'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                      {allAutoPassed && allAutoChecksDone && <polyline points="9 12 11 14 15 10" />}
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontSize: '19px', fontWeight: 700, color: '#111827' }}>
                      {isCheckingCompatibility
                        ? 'Checking your system…'
                        : allAutoPassed
                        ? 'All checks passed'
                        : 'Action required'}
                    </div>
                  </div>
                </div>
                <div style={{ fontSize: '15px', fontWeight: 500, color: '#6b7280', marginLeft: '52px' }}>
                  {isCheckingCompatibility
                    ? 'Please wait while we verify your setup'
                    : allAutoPassed && !proctoringEnabled
                    ? 'Your system is ready — click Begin Test'
                    : allAutoPassed && proctoringEnabled
                    ? 'Confirm the items below to unlock the test'
                    : 'Fix the issues below and retry'}
                </div>

                {/* Progress bar */}
                <div style={{ height: '3px', background: '#e5e7eb', borderRadius: '2px', margin: '22px 0 0' }}>
                  <div style={{
                    height: '100%',
                    width: `${progressPct}%`,
                    background: allAutoPassed && allAutoChecksDone ? '#1D9E75' : '#9ca3af',
                    borderRadius: '2px',
                    transition: 'width 0.5s ease',
                  }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0 0', fontSize: '12px', fontWeight: 500, color: '#9ca3af' }}>
                  <span>{passedChecks} of {totalChecks} complete</span>
                  <span style={{ color: allAutoPassed && allAutoChecksDone ? '#0F6E56' : '#6b7280', fontWeight: 600 }}>
                    {allAutoPassed && allAutoChecksDone ? 'Ready' : `${progressPct}%`}
                  </span>
                </div>
              </div>

              {/* Check rows */}
              <div style={{ padding: '20px 18px 14px' }}>
                <CheckRow
                  status={internetStatus}
                  label="Internet speed"
                  detail={internetCheck ? 'Connection is stable and fast' : internetStatus === 'fail' ? 'Too slow — minimum 2 Mbps required' : ''}
                  resultLabel={internetStatus === 'pass' ? 'Pass' : 'Failed'}
                />
                <CheckRow
                  status={browserStatus}
                  label="Browser"
                  detail={browserCheck ? 'Browser is compatible' : browserStatus === 'fail' ? 'Use Chrome 80+ or a modern browser' : ''}
                  resultLabel={browserStatus === 'pass' ? 'Pass' : 'Failed'}
                />
                {proctoringEnabled && (
                  <CheckRow
                    status={cameraStatus}
                    label="Camera & mic"
                    detail={cameraCheck ? 'Access granted' : cameraStatus === 'fail' ? 'Permission denied — allow in browser settings' : ''}
                    resultLabel={cameraStatus === 'pass' ? 'Pass' : 'Failed'}
                  />
                )}
                {proctoringEnabled && (
                  <CheckRow
                    status={backgroundStatus}
                    label="Plain background"
                    detail={plainBackgroundCheck ? 'Background looks good' : backgroundStatus === 'fail' ? 'Cluttered background — use a plain wall' : ''}
                    resultLabel={backgroundStatus === 'pass' ? 'Pass' : 'Failed'}
                  />
                )}
              </div>

              {/* Camera Preview - proctoring + checking */}
              {proctoringEnabled && isCheckingCompatibility && (
                <div style={{ margin: '0 12px 12px', borderRadius: '10px', overflow: 'hidden', height: '120px', background: '#0d1117', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                  <div style={{ position: 'absolute', top: '8px', right: '10px', display: 'flex', alignItems: 'center', gap: '4px', background: 'rgba(0,0,0,0.5)', padding: '3px 8px', borderRadius: '20px' }}>
                    <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#ef4444', animation: 'compat_blink 1.2s infinite' }} />
                    <span style={{ fontSize: '10px', color: '#ef4444', fontWeight: 500 }}>LIVE</span>
                  </div>
                </div>
              )}

              {/* Failure hint */}
              {!isCheckingCompatibility && !allAutoPassed && (
                <div style={{ margin: '0 12px 12px', padding: '10px 12px', borderRadius: '10px', background: '#fff4f0', border: '0.5px solid #F0997B' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#712B13', marginBottom: '2px' }}>How to fix</div>
                  <div style={{ fontSize: '13px', fontWeight: 500, color: '#993C1D', lineHeight: '1.6' }}>
                    {cameraStatus === 'fail'
                      ? 'Allow camera & mic access in your browser site settings, then click Retry.'
                      : backgroundStatus === 'fail'
                      ? 'Sit in front of a plain wall or background, then click Retry.'
                      : internetStatus === 'fail'
                      ? 'Check your network connection, then click Retry.'
                      : 'Resolve the failed checks above and click Retry.'}
                  </div>
                </div>
              )}

              {/* Manual confirmations - after checks done + proctoring */}
              {!isCheckingCompatibility && proctoringEnabled && allAutoPassed && (
                <div style={{ padding: '0 12px 4px', animation: 'compat_fadein 0.4s ease-out' }}>
                  <div style={{ height: '0.5px', background: '#e5e7eb', margin: '0 0 14px' }} />
                  <div style={{ fontSize: '12px', fontWeight: 600, color: '#9ca3af', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '10px' }}>
                    Confirm before starting
                  </div>

                  {/* Notification checkbox */}
                  <div
                    onClick={() => setNotificationsOff(!notificationsOff)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      padding: '10px 12px', borderRadius: '10px', cursor: 'pointer',
                      border: `0.5px solid ${notificationsOff ? '#5DCAA5' : '#e5e7eb'}`,
                      background: notificationsOff ? '#f0faf5' : '#f9fafb',
                      marginBottom: '6px', transition: 'all 0.2s',
                    }}
                  >
                    <div style={{
                      width: '18px', height: '18px', borderRadius: '5px', flexShrink: 0,
                      border: `1.5px solid ${notificationsOff ? '#1D9E75' : '#d1d5db'}`,
                      background: notificationsOff ? '#1D9E75' : '#ffffff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {notificationsOff && (
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="2 6 5 9 10 3" />
                        </svg>
                      )}
                    </div>
                    <span style={{ fontSize: '14px', fontWeight: 500, color: '#374151', userSelect: 'none' }}>
                      Notifications are turned off
                    </span>
                  </div>

                  {/* Battery checkbox */}
                  <div
                    onClick={() => setHasBattery(!hasBattery)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      padding: '10px 12px', borderRadius: '10px', cursor: 'pointer',
                      border: `0.5px solid ${hasBattery ? '#5DCAA5' : '#e5e7eb'}`,
                      background: hasBattery ? '#f0faf5' : '#f9fafb',
                      marginBottom: '6px', transition: 'all 0.2s',
                    }}
                  >
                    <div style={{
                      width: '18px', height: '18px', borderRadius: '5px', flexShrink: 0,
                      border: `1.5px solid ${hasBattery ? '#1D9E75' : '#d1d5db'}`,
                      background: hasBattery ? '#1D9E75' : '#ffffff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {hasBattery && (
                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="2 6 5 9 10 3" />
                        </svg>
                      )}
                    </div>
                    <span style={{ fontSize: '14px', fontWeight: 500, color: '#374151', userSelect: 'none' }}>
                      Device is charged or plugged in
                    </span>
                  </div>
                </div>
              )}

              {/* Footer buttons or text-only pass state */}
              {isCheckingCompatibility ? (
                <div style={{ padding: '14px 12px 24px', textAlign: 'center', animation: 'compat_fadein 0.4s ease-out' }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#94a3b8' }}>
                    Running system checks, please wait…
                  </div>
                </div>
              ) : allAutoPassed && allAutoChecksDone ? (
                <div style={{ padding: '14px 12px 24px', textAlign: 'center', animation: 'compat_fadein 0.4s ease-out' }}>
                  <div style={{ fontSize: '15px', fontWeight: 600, color: '#0F6E56', marginBottom: '8px' }}>
                    All checks passed
                    <span style={{ display: 'inline-block', marginLeft: '4px' }}>
                      <span style={{ display: 'inline-block', width: '4px', height: '4px', borderRadius: '50%', background: '#0F6E56', animation: 'compat_dp 1.3s infinite', animationDelay: '0s', marginRight: '2px' }} />
                      <span style={{ display: 'inline-block', width: '4px', height: '4px', borderRadius: '50%', background: '#0F6E56', animation: 'compat_dp 1.3s infinite', animationDelay: '0.2s', marginRight: '2px' }} />
                      <span style={{ display: 'inline-block', width: '4px', height: '4px', borderRadius: '50%', background: '#0F6E56', animation: 'compat_dp 1.3s infinite', animationDelay: '0.4s' }} />
                    </span>
                  </div>
                  <div style={{ fontSize: '13px', fontWeight: 500, color: '#6b7280' }}>
                    Moving to test…
                  </div>
                </div>
              ) : (
                <div style={{ padding: '14px 12px 24px', display: 'flex', gap: '8px' }}>
                  <button
                    onClick={performCompatibilityCheck}
                    disabled={isCheckingCompatibility}
                    style={{
                      flexShrink: 0, padding: '10px 18px', borderRadius: '10px',
                      fontSize: '14px', fontWeight: 600, cursor: isCheckingCompatibility ? 'not-allowed' : 'pointer',
                      border: '0.5px solid #d1d5db', background: '#f3f4f6',
                      color: isCheckingCompatibility ? '#9ca3af' : '#374151',
                      opacity: isCheckingCompatibility ? 0.6 : 1,
                    }}
                  >
                    Retry
                  </button>
                  <button
                    disabled={!compatibilityPassed || isCheckingCompatibility}
                    onClick={() => {
                      try {
                        sessionStorage.setItem(compatFlagKey, 'true');
                        sessionStorage.setItem(`assignment_${assignmentId}_start_time`, Date.now().toString());
                        const scopedKey = getCheatReasonsKey();
                        localStorage.removeItem(scopedKey);
                        localStorage.setItem('current_cheat_key', scopedKey);
                        const sid = studentData?.getStudentByEmailId?.id;
                        if (sid) {
                          sessionStorage.removeItem(`tabSwitchCount_${sid}_${assignmentId}`);
                        }
                      } catch {}
                      setTestStarted(true);
                    }}
                    style={{
                      flex: 1, padding: '10px', borderRadius: '10px',
                      fontSize: '14px', fontWeight: 700,
                      border: 'none', cursor: compatibilityPassed && !isCheckingCompatibility ? 'pointer' : 'not-allowed',
                      background: compatibilityPassed && !isCheckingCompatibility ? '#0F6E56' : '#e5e7eb',
                      color: compatibilityPassed && !isCheckingCompatibility ? '#E1F5EE' : '#9ca3af',
                      transition: 'background 0.2s',
                    }}
                  >
                    {isCheckingCompatibility ? 'Checking system…' : 'Begin test'}
                  </button>
                </div>
              )}

              {/* Error message */}
              {!compatibilityPassed && !isCheckingCompatibility && (
                <div style={{ textAlign: 'center', fontSize: '13px', fontWeight: 500, color: '#dc2626', padding: '0 24px 16px' }}>
                  Complete all checks and confirmations to continue
                </div>
              )}
            </div>
          </div>
        </div>

        <style>{`
          @keyframes compat_dp {
            0%, 80%, 100% { opacity: 0.3; transform: scale(0.6); }
            40% { opacity: 1; transform: scale(1); }
          }
          @keyframes compat_blink {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.2; }
          }
          @keyframes compat_fadein {
            from { opacity: 0; transform: translateY(-8px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes compat_shimmerBase {
            0% { background-position: 220% 0; }
            100% { background-position: -220% 0; }
          }
        `}</style>
      </AssignmentLayout>
    );
  }

  // ===================== MAIN TEST SCREEN =====================
  return (
    <div className="relative z-10 min-h-screen bg-white min-w-screen">
      <div className="flex flex-col gap-4 px-6 pt-10 md:flex-row md:items-start md:justify-between md:gap-6 max-md:px-5 max-md:pt-6">
        {topBarLeft && (
          <div className="min-w-0">
            {topBarLeft}
          </div>
        )}
        {!previewMode && !disableSubmit && (
          <div className="md:ml-auto">
            <Timer
              durationInHours={remainingDurationInHours}
              onTimeUp={handleSubmitByEndtimePassed}
            />
          </div>
        )}
      </div>

      {openModal && modalType === "submit" && (
        <ModalUI
          open
          heading="Submit Test"
          subtitle="Final review before you hand it in. Make sure you've answered everything you wanted to."
          items={[
            {
              title: "Confirm submission",
              desc: "Are you sure you want to submit the test?",
            },
            {
              title: "Action required",
              desc: "Once you confirm, this action cannot be undone.",
            },
          ]}
          secondaryButtonText="Cancel"
          primaryButtonText="Submit test"
          onClose={() => setOpenModal(false)}
          onConfirm={() => {
            setOpenModal(false);
            startSubmitProgressFlow();
          }}
        />
      )}

      {openModal && modalType === "leave" && (
        <ModalComponent
          showOverlay
          heading="Leave Test"
          subtitle="Please review and confirm leaving this test."
          sidebarMode="warning"
          sidebarBottomIcon={
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 9v4" />
              <path d="M12 17h.01" />
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3.05h16.94a2 2 0 0 0 1.71-3.05L13.71 3.86a2 2 0 0 0-3.42 0z" />
            </svg>
          }
          items={[
            {
              title: "Confirm exit",
              desc: "Are you sure you want to leave? Your assignment will be submitted.",
              icon: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
              ),
            },
            {
              title: "Action required",
              desc: "Once you confirm, this action cannot be undone.",
              icon: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              ),
            },
          ]}
          secondaryButtonText="Cancel"
          primaryButtonText="Leave"
          onSkip={() => setOpenModal(false)}
          onEnable={() => {
            handleSubmit(false);
          }}
        />
      )}

      {showSubmitProgressModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6"
          style={{
            background: "radial-gradient(circle at top, rgba(59,130,246,0.08), transparent 60%), rgba(0,0,0,0.35)",
            backdropFilter: "blur(4px)",
            WebkitBackdropFilter: "blur(4px)",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 560,
              minHeight: 400,
              borderRadius: 12,
              background: "#ffffff",
              boxShadow: "0 30px 60px rgba(2, 6, 23, 0.28)",
              padding: "24px 22px 20px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              position: "relative",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: submitProgressStage === "success" ? 36 : "50%",
                left: "50%",
                transform: submitProgressStage === "success"
                  ? "translate(-50%, 0) scale(1)"
                  : "translate(-50%, -50%) scale(1)",
                transition: "top 0.6s ease, transform 0.6s ease",
                zIndex: 2,
              }}
            >
              <div
                style={{
                  width: 116,
                  height: 116,
                  borderRadius: "50%",
                  border: submitProgressStage === "success"
                    ? "8px solid #34d399"
                    : "8px solid #e5e7eb",
                  borderTopColor: submitProgressStage === "loading" ? "#22d3ee" : "#34d399",
                  borderRightColor: submitProgressStage === "loading" ? "#34d399" : "#34d399",
                  animation: submitProgressStage === "loading" ? "submit_modal_spin 1s linear infinite" : "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#fff",
                }}
              >
                {submitProgressStage === "success" && (
                  <div
                    style={{
                      width: 72,
                      height: 72,
                      borderRadius: "50%",
                      background: "#eafcf2",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "inset 0 0 0 1px rgba(34,197,94,0.08)",
                    }}
                  >
                    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                )}
              </div>
            </div>

            <div
              style={{
                textAlign: "center",
                marginTop: submitProgressTextVisible ? "194px" : "214px",
                opacity: submitProgressTextVisible ? 1 : 0,
                transform: submitProgressTextVisible ? "translateY(0)" : "translateY(10px)",
                transition: "opacity 0.4s ease, transform 0.4s ease, margin-top 0.4s ease",
              }}
            >
              <div style={{ fontSize: 30, fontWeight: 800, color: "#0f172a", lineHeight: 1.1, marginBottom: 10 }}>
                {submitProgressTitle}
              </div>
              <div style={{ fontSize: 14, color: "#667085", fontWeight: 500 }}>
                {submitProgressMessage}
              </div>
              {submitProgressReason ? (
                <div
                  style={{
                    marginTop: 12,
                    display: "inline-flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 4,
                    padding: "10px 12px",
                    borderRadius: 10,
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    textAlign: "center",
                    minWidth: 280,
                  }}
                >
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    Why auto-submitted
                  </span>
                  <span style={{ fontSize: 14, color: "#334155", fontWeight: 600 }}>
                    {submitProgressReason.replace(/^Reason:\s*/i, "")}
                  </span>
                </div>
              ) : null}
            </div>

            <div style={{ width: "100%", display: "flex", justifyContent: "flex-end", marginTop: "auto", paddingTop: 20 }}>
              <button
                onClick={() => {
                  if (!submitProgressButtonVisible || !submitProgressSubmission) return;
                  try {
                    sessionStorage.removeItem("bt_submit_ui_pending");
                  } catch {}
                  setShowSubmitProgressModal(false);
                  finalizeSubmissionNavigation(submitProgressSubmission);
                }}
                disabled={!submitProgressButtonVisible}
                style={{
                  border: "none",
                  borderRadius: 14,
                  padding: "12px 36px",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 15,
                  background: submitProgressButtonVisible
                    ? "linear-gradient(135deg, #0f7ac4, #0c4f8f)"
                    : "linear-gradient(135deg, #94a3b8, #64748b)",
                  boxShadow: submitProgressButtonVisible
                    ? "0 10px 20px rgba(13, 78, 145, 0.28)"
                    : "none",
                  cursor: submitProgressButtonVisible ? "pointer" : "not-allowed",
                  opacity: submitProgressButtonVisible ? 1 : 0,
                  transform: submitProgressButtonVisible ? "translateY(0)" : "translateY(8px)",
                  transition: "opacity 0.3s ease, transform 0.3s ease, background 0.2s ease",
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {showBotWarning && (
        <ModalComponent
          showOverlay
          sidebarMode="warning"
          heading="Only the bot can complete it in a few minutes."
          subtitle="Your activity is considered malpractice. Do you want to end the test?"
          items={[
            {
              title: "Warning",
              desc: "Submitting right now is treated as malpractice.",
              icon: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 9v4" />
                  <path d="M12 17h.01" />
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3.05h16.94a2 2 0 0 0 1.71-3.05L13.71 3.86a2 2 0 0 0-3.42 0z" />
                </svg>
              ),
            },
            {
              title: "Continue safely",
              desc: "Choose Continue to keep working on your test.",
              icon: (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 5v14" />
                  <path d="M5 12h14" />
                </svg>
              ),
            },
          ]}
          secondaryButtonText="Continue"
          primaryButtonText="End Test"
          onSkip={() => handleBotWarningClose(false)}
          onEnable={() => handleBotWarningClose(true)}
        />
      )}

      {showTenMinReminder && (
        <ModalComponent
          showOverlay
          sidebarMode="warning"
          heading="Hurry up!"
          subtitle="Only 10 minutes left. Please review and submit your test."
          sidebarBottomIcon={
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2h12" />
              <path d="M6 22h12" />
              <path d="M8 2v4a4 4 0 0 0 1.6 3.2L12 11l2.4-1.8A4 4 0 0 0 16 6V2" />
              <path d="M16 22v-4a4 4 0 0 0-1.6-3.2L12 13l-2.4 1.8A4 4 0 0 0 8 18v4" />
            </svg>
          }
          items={[
            {
              title: "Time alert",
              desc: "You have 10 minutes remaining to complete this assignment.",
            },
          ]}
          primaryButtonText="OK"
          onEnable={handleTenMinReminderClose}
        />
      )}

      {showPanicPopup && <PanicPopup onClose={() => setShowPanicPopup(false)} />}

      {!previewMode && assignmentData?.getAssignment?.enable_proctoring && (
        <div className="pointer-events-none">
          <Detection
            max_cheat_count={assignmentData?.getAssignment?.max_cheat_count}
            handleSubmit={(auto = false, fromProctor = false) => {
              if (fromProctor) return handleSubmit('proctor');
              if (auto === true) return handleSubmit('tab');
              return handleSubmit('manual');
            }}
          />
        </div>
      )}

      {children}

      <style>{`
        @keyframes submit_modal_spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default TestLayout;